import { Add, Edit, OpenInNew, PersonOff, Person, Search } from '@mui/icons-material';
import {
    Box,
    Breadcrumbs,
    Button,
    Card,
    CardContent,
    Chip,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { ClearIcon } from '@mui/x-date-pickers';
import React, { useCallback, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../api';
import DidNotLoad from '../../components/DidNotLoad';
import Loading from '../../components/Loading';
import CustomPagination from '../../components/Pagination';
import { DEFAULT_LIST_PAGE_SIZE } from '../../constants/listPagination';
import { isMasterUser, patchStoredUser } from '../../helpers/authSession';
import { formatDate } from '../../helpers/formatters';
import { applyEmail, applyName } from '../../helpers/captureMode';
import { openMercadoPagoSubscription } from '../../helpers/mercadoPagoLinks';
import { useListFetch } from '../../hooks/useListFetch';

const PLAN_FILTERS = [
    { value: '', label: 'Todos' },
    { value: 'free', label: 'Free' },
    { value: 'growing', label: 'Growing' },
    { value: 'pro', label: 'Pro' },
    { value: 'farm', label: 'Farm' },
];

function planChipProps(planName) {
    const normalized = String(planName || 'Free').trim().toLowerCase();
    if (normalized === 'farm' || normalized === 'unlimited' || normalized === 'ilimitado') {
        return { label: 'Farm', color: 'warning' };
    }
    if (normalized === 'pro' || normalized === 'profissional') {
        return { label: 'Pro', color: 'secondary' };
    }
    if (normalized === 'growing' || normalized === 'crescendo') {
        return { label: 'Growing', color: 'info' };
    }
    return { label: 'Free', color: 'default' };
}

function pendingPlanChipLabel(user) {
    const pendingPlan = String(user?.pending_plan_name || '').trim();
    if (pendingPlan) {
        return `Pendente: ${pendingPlan}`;
    }
    return 'Assinatura pendente';
}

function hasPendingSubscription(user) {
    return Boolean(user?.plan_migration_pending);
}

function mpStatusChipProps(user) {
    const status = String(user?.mp_subscription_status || '').trim().toLowerCase();
    if (!status) {
        return null;
    }
    if (user?.mp_grace_until) {
        return { label: 'Grace', color: 'warning' };
    }
    if (user?.mp_trial_active && status === 'authorized') {
        return { label: 'Trial', color: 'info' };
    }
    if (status === 'authorized') {
        return { label: 'MP ativa', color: 'success' };
    }
    if (status === 'pending') {
        return { label: 'MP pendente', color: 'warning' };
    }
    if (status === 'paused') {
        return { label: 'MP pausada', color: 'default' };
    }
    if (status === 'cancelled') {
        return { label: 'MP cancelada', color: 'default' };
    }
    return { label: `MP ${status}`, color: 'default' };
}

const EMPTY_STATS = {
    total: 0,
    active_count: 0,
    inactive_count: 0,
    free_count: 0,
    growing_count: 0,
    pro_count: 0,
};

function ListUsersPage() {
    const [isMaster, setIsMaster] = useState(isMasterUser());
    const [checkingAccess, setCheckingAccess] = useState(!isMasterUser());
    const [users, setUsers] = useState([]);
    const [stats, setStats] = useState(EMPTY_STATS);
    const [isLoading, setIsLoading] = useState(true);
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [planFilter, setPlanFilter] = useState('');
    const [page, setPage] = useState(1);
    const [limit] = useState(DEFAULT_LIST_PAGE_SIZE);
    const [totalPages, setTotalPages] = useState(1);
    const [sortBy, setSortBy] = useState('created_at');
    const [sortOrder, setSortOrder] = useState('desc');
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    React.useEffect(() => {
        let cancelled = false;
        api.get('/auth/me')
            .then((response) => {
                const user = response?.data?.data;
                if (user && typeof user.is_master === 'boolean') {
                    patchStoredUser({ is_master: user.is_master });
                    if (!cancelled) {
                        setIsMaster(user.is_master);
                    }
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setIsMaster(false);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setCheckingAccess(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const fetchUsers = useCallback(() => {
        if (!isMaster) {
            return;
        }
        setIsLoading(true);
        api.get('/users', {
            params: {
                page,
                limit,
                q: appliedSearch,
                plan: planFilter,
                sort_by: sortBy,
                sort_order: sortOrder,
            },
        })
            .then((response) => {
                setUsers(Array.isArray(response?.data?.data) ? response.data.data : []);
                setTotalPages(Math.max(1, Number(response?.data?.total_pages) || 1));
                setStats({
                    ...EMPTY_STATS,
                    ...(response?.data?.stats || {}),
                });
                setDidNotLoad(false);
            })
            .catch(() => {
                setDidNotLoad(true);
                setUsers([]);
                setStats(EMPTY_STATS);
                setTotalPages(1);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [isMaster, page, limit, appliedSearch, planFilter, sortBy, sortOrder]);

    useListFetch(fetchUsers, [isMaster, fetchUsers]);

    const applySearch = () => {
        setPage(1);
        setAppliedSearch(searchTerm);
    };

    const handleSortByName = () => {
        setPage(1);
        if (sortBy !== 'name') {
            setSortBy('name');
            setSortOrder('asc');
            return;
        }
        if (sortOrder === 'asc') {
            setSortOrder('desc');
            return;
        }
        setSortBy('created_at');
        setSortOrder('desc');
    };

    const handleSortByStatus = () => {
        setPage(1);
        if (sortBy !== 'last_login_at') {
            setSortBy('last_login_at');
            setSortOrder('asc');
            return;
        }
        if (sortOrder === 'asc') {
            setSortOrder('desc');
            return;
        }
        setSortBy('created_at');
        setSortOrder('desc');
    };

    const handleToggleActive = async (user) => {
        const nextActive = !user.active;
        const result = await Swal.fire({
            title: nextActive ? 'Reativar usuário?' : 'Inativar usuário?',
            text: nextActive
                ? `${applyName(user.name) || applyEmail(user.email)} voltará a poder entrar.`
                : `${applyName(user.name) || applyEmail(user.email)} não poderá mais fazer login.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: nextActive ? 'Sim, reativar' : 'Sim, inativar',
            cancelButtonText: 'Cancelar',
        });
        if (!result.isConfirmed) {
            return;
        }
        try {
            await api.put(`/users/${user.uuid}`, { active: nextActive });
            fetchUsers();
            await Swal.fire({
                icon: 'success',
                title: nextActive ? 'Usuário reativado' : 'Usuário inativado',
                timer: 1600,
                showConfirmButton: false,
            });
        } catch (error) {
            await Swal.fire({
                icon: 'error',
                title: 'Falha',
                text: error?.response?.data?.error || 'Não foi possível atualizar o usuário.',
            });
        }
    };

    if (checkingAccess) {
        return <Loading />;
    }
    if (!isMaster) {
        return <Navigate to="/admin" replace />;
    }
    if (didNotLoad) {
        return <DidNotLoad />;
    }

    return (
        <Box>
            <Breadcrumbs sx={{ mb: 2 }}>
                <Link color="inherit" to="/admin" onClick={(e) => { e.preventDefault(); navigate('/admin'); }}>
                    Dashboard
                </Link>
                <Typography color="text.primary">Usuários</Typography>
            </Breadcrumbs>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="h5">Usuários</Typography>
                <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/admin/register-user')}>
                    Novo usuário
                </Button>
            </Box>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                <Card sx={{ flex: 1 }}>
                    <CardContent>
                        <Typography color="text.secondary" variant="body2">Total de usuários</Typography>
                        <Typography variant="h3" sx={{ my: 0.5 }}>{stats.total ?? 0}</Typography>
                        <Typography variant="body2">Ativos: {stats.active_count ?? 0}</Typography>
                        <Typography variant="body2">Inativos: {stats.inactive_count ?? 0}</Typography>
                    </CardContent>
                </Card>
                <Card sx={{ flex: 1 }}>
                    <CardContent>
                        <Typography color="text.secondary" variant="body2">Plano Free</Typography>
                        <Typography variant="h4" sx={{ mt: 1 }}>{stats.free_count ?? 0}</Typography>
                    </CardContent>
                </Card>
                <Card sx={{ flex: 1 }}>
                    <CardContent>
                        <Typography color="text.secondary" variant="body2">Plano Growing</Typography>
                        <Typography variant="h4" sx={{ mt: 1 }}>{stats.growing_count ?? 0}</Typography>
                    </CardContent>
                </Card>
                <Card sx={{ flex: 1 }}>
                    <CardContent>
                        <Typography color="text.secondary" variant="body2">Plano Pro</Typography>
                        <Typography variant="h4" sx={{ mt: 1 }}>{stats.pro_count ?? 0}</Typography>
                    </CardContent>
                </Card>
            </Stack>

            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                    size="small"
                    placeholder="Buscar por nome, e-mail, loja ou plano"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            applySearch();
                        }
                    }}
                    sx={{ minWidth: isMobile ? '100%' : 320 }}
                />
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Plano</InputLabel>
                    <Select
                        label="Plano"
                        value={planFilter}
                        onChange={(e) => {
                            setPage(1);
                            setPlanFilter(e.target.value);
                        }}
                    >
                        {PLAN_FILTERS.map((option) => (
                            <MenuItem key={option.value || 'all'} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button variant="outlined" startIcon={<Search />} onClick={applySearch}>
                    Buscar
                </Button>
                <IconButton
                    onClick={() => {
                        setSearchTerm('');
                        setAppliedSearch('');
                        setPlanFilter('');
                        setPage(1);
                        setSortBy('created_at');
                        setSortOrder('desc');
                    }}
                    aria-label="Limpar filtros"
                >
                    <ClearIcon />
                </IconButton>
            </Box>

            {isLoading ? (
                <Loading />
            ) : (
                <>
                    <TableContainer component={Paper}>
                        <Table size={isMobile ? 'small' : 'medium'}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sortDirection={sortBy === 'name' ? sortOrder : false}>
                                        <TableSortLabel
                                            active={sortBy === 'name'}
                                            direction={sortBy === 'name' ? sortOrder : 'asc'}
                                            onClick={handleSortByName}
                                        >
                                            Nome
                                        </TableSortLabel>
                                    </TableCell>
                                    {!isMobile && <TableCell>E-mail</TableCell>}
                                    <TableCell>Plano</TableCell>
                                    {!isMobile && <TableCell>MP</TableCell>}
                                    {!isMobile && <TableCell>Contadores</TableCell>}
                                    <TableCell sortDirection={sortBy === 'last_login_at' ? sortOrder : false}>
                                        <TableSortLabel
                                            active={sortBy === 'last_login_at'}
                                            direction={sortBy === 'last_login_at' ? sortOrder : 'asc'}
                                            onClick={handleSortByStatus}
                                        >
                                            Status
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right">Ações</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {users.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={isMobile ? 4 : 7}>
                                            <Typography variant="body2" color="text.secondary">
                                                Nenhum usuário encontrado.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    users.map((user) => {
                                        const plan = planChipProps(user.plan_name);
                                        const mpStatus = mpStatusChipProps(user);
                                        return (
                                            <TableRow key={user.uuid} hover>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {applyName(user.name) || '—'}
                                                    </Typography>
                                                    {isMobile && (
                                                        <Typography variant="caption" color="text.secondary" display="block">
                                                            {applyEmail(user.email)}
                                                        </Typography>
                                                    )}
                                                    {isMobile && (
                                                        <Typography
                                                            variant="caption"
                                                            color="primary"
                                                            display="block"
                                                            sx={{ mt: 0.5, cursor: 'pointer', textDecoration: 'underline' }}
                                                            onClick={() => navigate(`/admin/users/${user.uuid}/assets`)}
                                                        >
                                                            {`${user.printers_count ?? 0} impressoras · ${user.jobs_count ?? 0} impressões · ${user.products_count ?? 0} produtos`}
                                                        </Typography>
                                                    )}
                                                    {user.is_master && (
                                                        <Chip size="small" label="Master" color="warning" sx={{ mt: 0.5 }} />
                                                    )}
                                                </TableCell>
                                                {!isMobile && <TableCell>{applyEmail(user.email)}</TableCell>}
                                                <TableCell>
                                                    <Stack spacing={0.5} alignItems="flex-start">
                                                        <Chip size="small" label={plan.label} color={plan.color} />
                                                        {hasPendingSubscription(user) ? (
                                                            <Chip
                                                                size="small"
                                                                label={pendingPlanChipLabel(user)}
                                                                color="warning"
                                                                variant="outlined"
                                                            />
                                                        ) : null}
                                                        {isMobile && mpStatus ? (
                                                            <Chip size="small" label={mpStatus.label} color={mpStatus.color} variant="outlined" />
                                                        ) : null}
                                                    </Stack>
                                                </TableCell>
                                                {!isMobile && (
                                                    <TableCell>
                                                        {mpStatus ? (
                                                            <Chip size="small" label={mpStatus.label} color={mpStatus.color} />
                                                        ) : (
                                                            <Typography variant="body2" color="text.secondary">—</Typography>
                                                        )}
                                                    </TableCell>
                                                )}
                                                {!isMobile && (
                                                    <TableCell
                                                        onClick={() => navigate(`/admin/users/${user.uuid}/assets`)}
                                                        sx={{
                                                            cursor: 'pointer',
                                                            '&:hover': { bgcolor: 'action.hover' },
                                                        }}
                                                        title="Ver impressoras, impressões e produtos"
                                                    >
                                                        <Typography variant="body2" lineHeight={1.4} color="primary.main">
                                                            {user.printers_count ?? 0} impressoras
                                                        </Typography>
                                                        <Typography variant="body2" lineHeight={1.4} color="primary.main">
                                                            {user.jobs_count ?? 0} impressões
                                                        </Typography>
                                                        <Typography variant="body2" lineHeight={1.4} color="primary.main">
                                                            {user.products_count ?? 0} produtos
                                                        </Typography>
                                                    </TableCell>
                                                )}
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        label={user.active === false ? 'Inativo' : 'Ativo'}
                                                        color={user.active === false ? 'default' : 'success'}
                                                    />
                                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                                                        {user.last_login_at
                                                            ? `Último login: ${formatDate(user.last_login_at)}`
                                                            : 'Último login: --'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    {user.mp_preapproval_id ? (
                                                        <Tooltip title="Abrir no Mercado Pago">
                                                            <IconButton onClick={() => openMercadoPagoSubscription(user.mp_preapproval_id)}>
                                                                <OpenInNew fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    ) : null}
                                                    <Tooltip title="Editar">
                                                        <IconButton onClick={() => navigate(`/admin/edit-user/${user.uuid}`)}>
                                                            <Edit fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    {!user.is_master && (
                                                        <Tooltip title={user.active === false ? 'Reativar' : 'Inativar'}>
                                                            <IconButton onClick={() => handleToggleActive(user)}>
                                                                {user.active === false
                                                                    ? <Person fontSize="small" />
                                                                    : <PersonOff fontSize="small" />}
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <CustomPagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
                </>
            )}
        </Box>
    );
}

export default ListUsersPage;
